import { orderBy } from 'lodash'

import { EvmAddress } from '~/utils/address'

export type UpgradeScenario = 'satisfied' | 'single' | 'multi' | 'insufficient'

export type UpgradeAccount = {
  address: EvmAddress.Type
  name: string
  usdcBalance: number
  /** USDC this account contributes to the deposit; 0 when it isn't needed */
  depositAmount: number
}

const roundCents = (value: number) => Math.round(value * 100) / 100

// Deposits are USDC token amounts, not USD values.
export const formatUsdc = (amount: number) => {
  const decimals = Number.isInteger(amount) ? 0 : 2
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

// How much of a plan's Earn minimum is covered, from the page's own shortfall
// (`amountNeeded`) so the bar always agrees with the deposit amounts. A
// shortfall can exceed the minimum only in preview fixtures; it's clamped so
// the bar stays within 0-100%.
export const getEarnProgress = ({
  earnMinimum,
  amountNeeded,
}: {
  earnMinimum: number
  amountNeeded: number
}) => {
  const remaining = Math.min(Math.max(amountNeeded, 0), earnMinimum)
  const held = roundCents(earnMinimum - remaining)
  const percent = earnMinimum > 0 ? (held / earnMinimum) * 100 : 100
  return { held, remaining, percent: Math.min(Math.max(percent, 0), 100) }
}

// Mirrors the server's plan_deposit allocation: largest balance first, each
// account covering what it can of the remaining amount.
export const getUpgradeScenario = <
  T extends { address: EvmAddress.Type; usdcBalance: number },
>({
  amountNeeded,
  accounts,
}: {
  amountNeeded: number
  accounts: T[]
}): {
  scenario: UpgradeScenario
  accounts: (T & { depositAmount: number })[]
} => {
  const ordered = orderBy(accounts, 'usdcBalance', 'desc')
  const { allocated } = ordered.reduce<{
    remaining: number
    allocated: (T & { depositAmount: number })[]
  }>(
    (acc, account) => {
      const depositAmount = roundCents(
        Math.min(account.usdcBalance, acc.remaining),
      )
      return {
        remaining: roundCents(acc.remaining - depositAmount),
        allocated: [...acc.allocated, { ...account, depositAmount }],
      }
    },
    { remaining: Math.max(amountNeeded, 0), allocated: [] },
  )
  const contributors = allocated.filter((account) => account.depositAmount > 0)
  const totalBalance = roundCents(
    accounts.reduce((sum, account) => sum + account.usdcBalance, 0),
  )

  if (amountNeeded <= 0) return { scenario: 'satisfied', accounts: [] }
  if (totalBalance < amountNeeded)
    return { scenario: 'insufficient', accounts: allocated }
  return {
    scenario: contributors.length === 1 ? 'single' : 'multi',
    accounts: contributors,
  }
}
